import * as React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import type { SocialDiscoveryFeedQuery, SocialUserFeedQuery } from '@web/gen/feed'
import {
  socialDiscoveryFeedQueryFetcher,
  socialDiscoveryFeedQueryKey,
  socialDiscoveryFeedQueryRootKey,
  socialUserFeedQueryFetcher,
  socialUserFeedQueryKey,
  socialUserFeedQueryRootKey,
  socialFollowersFeedQueryFetcher,
  socialFollowersFeedQueryKey,
  socialFollowersFeedQueryRootKey,
  socialSavedFeedQueryFetcher,
  socialSavedFeedQueryKey,
  socialSavedFeedQueryRootKey,
} from '@web/gen/feed'

interface SocialDiscoveryFeedInterface {
  initialData?: InfiniteData<SocialDiscoveryFeedQuery>
}

type Cursor = number | null | undefined
type PageParam =
  | { input: { cursor: number | null | undefined; itemsPerPage: number; userSlug: string } }
  | number
  | null
  | undefined

export function useSocialDiscoveryFeedQuery({ initialData }: SocialDiscoveryFeedInterface) {
  const variables = { input: { itemsPerPage: 10 } }

  const normalizedInitialData = React.useMemo(() => {
    if (!initialData) {
      return
    }
    // oxlint-disable-next-line array-callback-return
    const pageParams = (initialData.pageParams as unknown[]).map(p => {
      // oxlint-disable-next-line eqeqeq
      if (p == null) {
        return
      }
      if (typeof p === 'number') {
        return p
      }
      const n = Number(p)
      return Number.isFinite(n) ? n : undefined
    }) as (number | null | undefined)[]
    return {
      pages: initialData.pages,
      pageParams,
    } as InfiniteData<SocialDiscoveryFeedQuery, number | null | undefined>
  }, [initialData])

  return useInfiniteQuery({
    queryKey: [
      `${socialDiscoveryFeedQueryRootKey}.Infinite`,
      ...socialDiscoveryFeedQueryKey(variables),
    ],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      socialDiscoveryFeedQueryFetcher({ input: { ...variables.input, cursor: pageParam } })(),
    getNextPageParam: lastPage => lastPage.socialDiscoveryFeed.pagination?.nextPageCursor,
    initialData: normalizedInitialData,
    initialDataUpdatedAt: 0,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useSocialUserFeedQuery({
  slug,
  initialData,
}: {
  slug: string
  initialData: SocialUserFeedQuery
}) {
  const variables = {
    input: {
      userSlug: slug,
      itemsPerPage: 10,
    },
  }

  return useInfiniteQuery({
    queryKey: [`${socialUserFeedQueryRootKey}.Infinite`, ...socialUserFeedQueryKey(variables)],
    initialPageParam: undefined as Cursor,
    queryFn: ({ pageParam }) =>
      socialUserFeedQueryFetcher({
        input: {
          ...variables.input,
          cursor:
            pageParam && typeof pageParam === 'object'
              ? (pageParam as { input?: { cursor?: number | null } }).input?.cursor
              : (pageParam as number | null | undefined),
        },
      })(),
    getNextPageParam: lastPage => {
      const lastCursor = lastPage.socialUserFeed.pagination?.nextPageCursor ?? undefined
      // oxlint-disable-next-line eqeqeq
      if (lastCursor == null) {
        return
      }
      return {
        input: {
          cursor: lastCursor,
          itemsPerPage: variables.input.itemsPerPage,
          userSlug: variables.input.userSlug,
        },
      }
    },
    initialData: {
      pages: [initialData],
      pageParams: [undefined as PageParam],
    },
    initialDataUpdatedAt: 0,
    staleTime: 60,
  })
}

export function useSocialFollowersFeedQuery(enabled: boolean) {
  const variables = { input: { itemsPerPage: 10 } }

  return useInfiniteQuery({
    queryKey: [
      `${socialFollowersFeedQueryRootKey}.Infinite`,
      ...socialFollowersFeedQueryKey(variables),
    ],
    enabled,
    initialPageParam: undefined as Cursor,
    queryFn: ({ pageParam }) =>
      socialFollowersFeedQueryFetcher({
        input: { ...variables.input, cursor: pageParam ?? undefined },
      })(),
    getNextPageParam: lastPage =>
      lastPage.socialFollowersFeed.pagination?.nextPageCursor ?? undefined,
    initialDataUpdatedAt: 0,
    staleTime: 60,
  })
}

export function useSocialSavedFeedQuery() {
  const variables = {
    input: {
      itemsPerPage: 10,
    },
  }

  return useInfiniteQuery({
    queryKey: [`${socialSavedFeedQueryRootKey}.Infinite`, ...socialSavedFeedQueryKey(variables)],
    queryFn: ({ pageParam }) => {
      return socialSavedFeedQueryFetcher({
        input: {
          ...variables.input,
          cursor: pageParam,
        },
      })()
    },
    getNextPageParam: lastPage => lastPage.socialSavedFeed.pagination?.nextPageCursor ?? undefined,
    initialPageParam: undefined as Cursor,
  })
}
